import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { dashboardService, todoSetupService } from '../services/api';
import AnalyticsSummary from '../components/Analytics/AnalyticsSummary';

const Home = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    callInboundToday: 0,
    messageInboundToday: 0,
    upcomingAppointments: 0,
    todayAppointments: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [todoData, setTodoData] = useState(null);
  const [todoLoading, setTodoLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [shouldShowTodo, setShouldShowTodo] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const [statsResponse, activitiesResponse] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getRecentActivity(5)
        ]);
        setStats({
          callInboundToday: statsResponse.call_inbound_today,
          messageInboundToday: statsResponse.message_inbound_today,
          upcomingAppointments: statsResponse.upcoming_appointments,
          todayAppointments: statsResponse.today_appointments
        });
        setRecentActivities(activitiesResponse.items || []);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setRecentActivities([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Load TODO setup data with localStorage logic
  useEffect(() => {
    const checkShouldShowTodoSetup = () => {
      const setupCompletedDate = localStorage.getItem('todoSetupCompletedDate');
      if (setupCompletedDate) {
        const completedDate = new Date(setupCompletedDate);
        const today = new Date();
        const timeDiff = today.getTime() - completedDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

        // If setup was completed more than 1 day ago, don't show it
        if (daysDiff > 1) {
          setShouldShowTodo(false);
          return;
        }
      }

      fetchTodoSetupStatus();
    };

    const fetchTodoSetupStatus = async () => {
      try {
        setTodoLoading(true);
        const data = await todoSetupService.getTodoSetupStatus();
        console.log('data', data);
        setTodoData(data);

        // If setup is 100% complete, mark the date in localStorage
        console.log('Setting todo setup completed date');
        if (data.completion_percentage === 100) {
          const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
          localStorage.setItem('todoSetupCompletedDate', today);
        }
      } catch (err) {
        console.error('Error fetching TODO setup status:', err);
        setTodoData(null);
      } finally {
        setTodoLoading(false);
      }
    };

    checkShouldShowTodoSetup();
  }, []);

  const handleCallsTodayClick = () => {
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    navigate(`/call-logs?date_from=${today}&date_to=${today}`);
  };

  const handleTodoItemClick = (todoItem) => {
    switch (todoItem.id) {
      case 'temp_user':
        navigate('/settings');
        break;
      case 'doctors':
        navigate('/doctors');
        break;
      case 'schedules':
        navigate('/calendar');
        break;
      case 'qa_knowledge_base':
        navigate('/policy');
        break;
      default:
        break;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high':
        return t('setup.highPriority');
      case 'medium':
        return t('setup.mediumPriority');
      case 'low':
        return t('setup.lowPriority');
      default:
        return t('setup.priority');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#075e54]"></div>
        <p className="ml-4 text-gray-600">{t('dashboard.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('dashboard.title')}</h1>
        <p className="text-gray-600">{t('dashboard.welcome')}, {currentUser?.username || 'User'}</p>
      </div>

      {/* Analytics Summary */}
      <AnalyticsSummary />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
          onClick={handleCallsTodayClick}
          title={t('dashboard.viewCallsToday')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('dashboard.callsToday')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.callInboundToday}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('dashboard.messagesInbound')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.messageInboundToday}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('dashboard.upcomingAppointments')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.upcomingAppointments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('dashboard.todayAppointments')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayAppointments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* TODO Setup Section */}
      {shouldShowTodo && todoData && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">{t('setup.title')}</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{todoData.completed_checks} of {todoData.total_checks} {t('setup.tasksCompleted')}</span>
              <span className="text-sm font-medium text-gray-700">{todoData.completion_percentage}%</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${todoData.completion_percentage}%` }}
              ></div>
            </div>
          </div>

          {/* Completion Status */}
          {todoData.completion_percentage === 100 && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    {t('setup.complete')}
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    {t('setup.completeMessage')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TODO Items */}
          <div className="space-y-3">
            {todoData.todo_items.map((item) => (
              <div
                key={item.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                  item.completed
                    ? 'bg-green-50 border-green-200'
                    : 'bg-white border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => handleTodoItemClick(item)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {item.completed ? (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-sm font-medium ${
                        item.completed ? 'text-green-800' : 'text-gray-900'
                      }`}>
                        {item.title}
                      </h3>
                      <p className={`text-xs ${
                        item.completed ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!item.completed && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item.priority)}`}>
                        {getPriorityText(item.priority)}
                      </span>
                    )}
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activities */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('dashboard.recentActivity')}</h2>
        {recentActivities.length === 0 ? (
          <p className="text-gray-500">{t('common.noData')}</p>
        ) : (
          <div className="space-y-3">
            {recentActivities.map((activity, index) => (
              <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[#075e54] rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {activity.type?.charAt(0).toUpperCase() || 'A'}
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.description}</p>
                </div>
                <div className="text-sm text-gray-400">
                  {activity.timestamp && new Date(activity.timestamp).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
